import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { AppLayout } from "../components/layout";
import {
  Button,
  Card,
  CardContent,
  Modal,
} from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Edit,
  Trash2,
  Eye,
  EyeOff,
  BookOpen,
  AlertCircle,
  ExternalLink,
  } from "lucide-react";
import { getGuides } from "../services/api";
import type { Guide } from "../types";
import { EmptyState } from "@/components/ui/empty-state";
import { useUIStore } from "../store/uiStore";
import { useGuideStore } from "../store/guideStore";
import { getGuidePublicUrl } from "@/lib/utils";
import { usePenpal } from "@/hooks/usePenpal";
import { useNavigateWithLock } from "../hooks/useNavigateWithLock";

const GuidesPage: React.FC = () => {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [guideToDelete, setGuideToDelete] = useState<string | null>(null);
  const { setLoading: setUILoading, openModal, closeModal, modals } = useUIStore();
  const navigate = useNavigateWithLock();
  const { deleteGuide } = useGuideStore();
  const { openExternalUrl } = usePenpal();
  const breadcrumbs = [
    // { title: "Dashboard", href: "dashboard" },
    { title: "Guides", isCurrentPage: true },
  ];

  useEffect(() => {
    fetchGuides();
  }, []);

  const fetchGuides = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getGuides();
      if (response.ok && response.guides) {
        const guides = response.guides.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        setGuides(guides);
      } else {
        setError(response.error || "Failed to fetch guides");
      }
    } catch (err) {
      console.error("Error fetching guides:", err);
      setError("Failed to fetch guides");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGuide = (guideId: string) => {
    setGuideToDelete(guideId);
    openModal('delete');
  };

  const handleConfirmDelete = async () => {
    if (!guideToDelete) return;
    
    closeModal('delete');
    setIsDeleting(true);
    
    try {
      const result = await deleteGuide(guideToDelete);
      if (result) {
        toast.success("Guide deleted from the cloud", {
          position: "top-center",
          duration: 3000,
        });
        
        // Refresh the guides list
        await fetchGuides();
        setGuideToDelete(null);
      } else {
        toast.error("Something went wrong during deletion", {
          position: "top-center",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error deleting guide:', error);
      toast.error("Something went wrong during deletion", {
        position: "top-center",
        duration: 3000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    closeModal('delete');
    setGuideToDelete(null);
  };

  const handleEditGuide = (guideId: string) => {
    navigate(`/guides/${guideId}/edit`);
  };

  const handleViewGuide = (guide: Guide) => {
    const url = getGuidePublicUrl(guide);
    openExternalUrl(url);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };


  if (loading) {
    return (
      <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>
          <div className="space-y-2">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="flex items-center space-x-4 p-4 border rounded-lg"
              >
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-8 w-24" />
              </div>
            ))}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-xl font-bold text-foreground">Guides</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Manage your guides
            </p>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <Card className="border-destructive mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Guides Table */}
        {guides.length === 0 && !error ? (
          <EmptyState
            title="No guides published yet"
            description="To publish guide online, go back to the app, open any guide and choose Export -> Online"
            icons={[BookOpen, BookOpen, BookOpen]}
            className="mx-auto"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-1.5 px-2 font-medium text-sm text-muted-foreground">
                    Title
                  </th>
                  <th className="text-left py-1.5 px-2 font-medium text-sm text-muted-foreground">
                    Updated
                  </th>
                  <th className="text-left py-1.5 px-2 font-medium text-sm text-muted-foreground">
                    Status
                  </th>
                  <th className="text-left py-1.5 px-2 font-medium text-sm text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {guides.map((guide) => (
                  <tr
                    key={guide.id}
                    className="border-b hover:bg-muted/50 transition-colors"
                  >
                    <td className="py-2 px-2">
                      <div className="font-medium text-foreground text-sm">
                        {guide.title}
                      </div>
                    </td>
                    <td className="py-2 px-2">
                      <div className="text-sm text-muted-foreground">
                        {formatDate(guide.updatedAt)}
                      </div>
                    </td>
                    <td className="py-2 px-2">
                      {guide.isPublished ? (
                        <Badge variant="default" className="text-xs">
                          <Eye className="h-3 w-3 mr-1" />
                          Published
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          <EyeOff className="h-3 w-3 mr-1" />
                          Draft
                        </Badge>
                      )}
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewGuide(guide)}
                          className="h-8 w-8 p-0"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditGuide(guide.internalId!)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteGuide(guide.id)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={modals.delete}
        onClose={handleCancelDelete}
        title=""
        noHeader
        size="md"
      >
        <div className="px-4 my-4 text-black text-sm">
          Do you wish to delete this guide from the cloud? This won't remove any
          local files, only information and images in the cloud
        </div>
        <div className="flex justify-end space-x-3 p-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleCancelDelete}
            disabled={isDeleting}
            style={{ backgroundColor: '#efefef' }}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            loading={isDeleting}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </AppLayout>
  );
};

export default GuidesPage;
